/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement, useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import NavBarLanguageSelectionView from './NavBarLanguageSelection/NavBarLanguageSelectionView';
import NavBarTitleView from './NavBarTitle/NavBarTitleView';
import { Container } from '@mui/material';
import NavigationBarStyles from './NavigationBarStyles';
import ProfileView from './Profile/ProfileView';
import NotificationBellView from './NotificationBell/NotificationBellView';
import {
	DISMISSED_NOTIFICATIONS_KEY,
	DISMISSED_NOTIFICATIONS_RESET_EVENT,
	DISMISSED_NOTIFICATIONS_UPDATED_EVENT,
} from '../../consts/notifications';
import { useLocation } from 'react-router-dom';
import { Pages } from '../../navigation/Pages';

const NavBarView = (): ReactElement => {
	const { classes } = NavigationBarStyles();
	const location = useLocation();
	/**
	 * Normalizes the current route path to avoid trailing slash mismatches.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const normalizePath = (path: string): string => {
		if (path.length > 1 && path.endsWith('/')) {
			return path.slice(0, -1);
		}
		return path;
	};
	const [dismissedCount, setDismissedCount] = useState(0);
	const normalizedPath = normalizePath(location.pathname);
	const isCoursesOverviewRoute = normalizedPath === Pages.COURSES_OVERVIEW;

	// Load dismissed count from localStorage
	const loadDismissedCount = (): void => {
		try {
			const stored = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
			const dismissed = stored ? JSON.parse(stored) : [];
			setDismissedCount(dismissed.length);
		} catch {
			setDismissedCount(0);
		}
	};

	// Load on mount and listen for storage changes
	useEffect(() => {
		loadDismissedCount();

		const handleUpdate = (): void => {
			loadDismissedCount();
		};

		window.addEventListener('storage', handleUpdate);
		window.addEventListener(DISMISSED_NOTIFICATIONS_UPDATED_EVENT, handleUpdate);

		return () => {
			window.removeEventListener('storage', handleUpdate);
			window.removeEventListener(DISMISSED_NOTIFICATIONS_UPDATED_EVENT, handleUpdate);
		};
	}, []);

	/**
	 * Resets all dismissed notifications.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	const handleResetNotifications = (): void => {
		localStorage.removeItem(DISMISSED_NOTIFICATIONS_KEY);
		setDismissedCount(0);
		// Dispatch custom events to notify other components
		window.dispatchEvent(new Event(DISMISSED_NOTIFICATIONS_RESET_EVENT));
		window.dispatchEvent(new Event(DISMISSED_NOTIFICATIONS_UPDATED_EVENT));
	};

	return (
		<AppBar position='static' className={classes.root}>
			<Container className={classes.container}>
				<Toolbar className={classes.toolbar}>
					<NavBarTitleView />
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						{isCoursesOverviewRoute && (
							<NotificationBellView dismissedCount={dismissedCount} onResetNotifications={handleResetNotifications} />
						)}
						<NavBarLanguageSelectionView />
						<ProfileView />
					</div>
				</Toolbar>
			</Container>
		</AppBar>
	);
};

export default NavBarView;
